import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { requestNotificationPermission } from "../firebase";
import Sidebar from "../components/Sidebar.jsx";
import { Sun, Moon, User, DollarSign } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
// ================================
// 🔥 TELEGRAM-PREMIUM STYLE ICON
// ================================
import ffDiamond from "../assets/icons/ff-diamond.png";
import simCard from "../assets/icons/sim-card.png";
import paymentCard from "../assets/icons/payment-card.png";
import kycIcon from "../assets/icons/kyc.png"; // 🆕 KYC IMAGE
import siteLogo from "../assets/icons/logo.png";

const PremiumIcon = ({ type }) => {
  const icons = {
    facebook: "https://cdn-icons-png.flaticon.com/512/5968/5968764.png",
    youtube: "https://cdn-icons-png.flaticon.com/512/1384/1384060.png",
    instagram: "https://cdn-icons-png.flaticon.com/512/2111/2111463.png",
    tiktok: "https://cdn-icons-png.flaticon.com/512/3046/3046121.png",
    telegram: "https://cdn-icons-png.flaticon.com/512/2111/2111646.png",

    // ✅ LOCAL IMAGES (NO FAIL)
    diamond: ffDiamond,
    sim: simCard,
    payment: paymentCard,

    // 🆕 KYC SERVICE
    kyc: kycIcon,

    verify: "https://cdn-icons-png.flaticon.com/512/1828/1828640.png",
  };

  return (
    <div className="premium-icon-wrap">
      <div className="premium-glow" />
      <img
        src={icons[type]}
        alt={type}
        className="premium-icon-img"
        onError={(e) => {
          e.target.style.display = "none";
          console.error("Icon load failed:", type);
        }}
      />
    </div>
  );
};
export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const categorySectionRef = useRef(null);
  const [matchedCategories, setMatchedCategories] = useState([]);
  const [noServiceMessage, setNoServiceMessage] = useState("");
  // ===============================
// 🔥 CATEGORY MATCHING HELPERS (NEW)
// ===============================

// 🧹 Emoji + special char safe normalize
const normalizeText = (text = "") =>
  text
    .toLowerCase()
    .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// 🔑 Keyword aliases (smart matching)
const keywordAliases = {
  facebook: ["facebook", "fb"],
  youtube: ["youtube", "yt"],
  tiktok: ["tiktok", "tik tok", "tt"],
  instagram: ["instagram", "insta", "ig"],
  telegram: ["telegram", "tg"],
  payment: ["payment", "bkash", "nagad", "rocket"],
  sim: ["sim", "number"],
  diamond: ["ff", "free fire", "diamond"],
  kyc: ["kyc"],
  verify: ["verify", "verified"],
};
 /* ============================
     Back / Protected-route Logic (Final Stable)
     - Prevents navigating back to login/register while logged in
     - Blocks leaving dashboard without logout
  ============================ */
 useEffect(() => {
  const protectedPaths = [
    "/",
    "/dashboard",
    "/orders",
    "/addfunds",
    "/support",
    "/payment",
    "/account",
    "/terms",
  ];

  const handleBackButton = (e) => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    const currentPath = window.location.pathname;

    // 🔒 যদি লগইন না করা থাকে, সরাসরি login পেজে পাঠাবে
    if (!token || !user) {
      window.location.href = "/login";
      return;
    }

    // ✅ Suspended user alert না পাবে
    if (protectedPaths.includes(currentPath)) {
      const parsedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (parsedUser.status !== "suspended") {
        e.preventDefault();
        window.history.pushState(null, "", currentPath);
        alert("⚠️ Logout না করা পর্যন্ত আপনি Dashboard থেকে বের হতে পারবেন না!");
      }
    }
  };

  // 🔁 প্রথমে বর্তমান পেজ lock করো (back করলে আগের auth পেজে না যায়)
  window.history.pushState(null, "", window.location.pathname);
  window.addEventListener("popstate", handleBackButton);

  return () => {
    window.removeEventListener("popstate", handleBackButton);
  };
}, []);
  // ============================
  // React States (kept same names so rest of file works unchanged)
  // ============================
  // 🔥 HYDRATE FROM CACHE FIRST (NO BLANK UI)
const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
})();

const storedOrders = (() => {
  try {
    return JSON.parse(localStorage.getItem("orders")) || [];
  } catch {
    return [];
  }
})();

const [username, setUsername] = useState(storedUser.username || "");
const [balance, setBalance] = useState(
  storedUser.balance !== undefined ? Number(storedUser.balance) : null
);

const [spend, setSpend] = useState(
  storedUser.spend !== undefined ? Number(storedUser.spend) : null
);
const [totalOrders, setTotalOrders] = useState(storedOrders.length || 0);
const [loadingUser, setLoadingUser] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [accountUrl, setAccountUrl] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [darkMode, setDarkMode] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // 🔔 Notification System
  const [notifications, setNotifications] = useState([
  { id: 1, text: "🎉 Your last order has been completed!", read: false },
  { id: 2, text: "💰 Your fund was approved successfully!", read: true },
  { id: 3, text: "❌ One of your orders was cancelled!", read: false }
]);
// ===============================
// 🔥 Clear Notifications Function
// ===============================
const clearNotifications = async () => {
  try {
    const token = localStorage.getItem("token");

    await axios.delete("https://api.speedfollowinfo.com/api/notifications/clear", {
      headers: { Authorization: `Bearer ${token}` }
    });

    // UI থেকেও নোটিফিকেশন খালি করে দাও
    setNotifications([]);

    // রিফ্রেশে আর আসে না — localStorage এ ফাঁকা হিসেবে সেভ করে দেই
    localStorage.setItem("notifications", JSON.stringify([]));

  } catch (err) {
    console.error("❌ Clear Error:", err);
  }
};

  const [showNotifications, setShowNotifications] = useState(false);
  const notificationSound = new Audio("/notification.mp3");
notificationSound.volume = 1.0; // Full volume
// Notification Ref (to detect outside click)
  const notifRef = useRef(null);
  // 🔔 User Notifications From Backend
  const [userNotifications, setUserNotifications] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderPopup, setOrderPopup] = useState(null);
  const [lowBalancePopup, setLowBalancePopup] = useState(null);
  

  // balance check + order button condition (keeps parity with your later code)
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  });
  const [insufficient, setInsufficient] = useState(false);

// ✅ Announcement system (admin থেকে আসা বার্তা দেখানোর জন্য)
const [announcement, setAnnouncement] = useState(() => {
  const saved = localStorage.getItem("announcement");
  return saved ? saved : "";
});
// 🔒 Order Lock (Admin Panel → User Panel Control)
const [orderLock, setOrderLock] = useState(false);

// Load order lock from backend
useEffect(() => {
  const loadOrderLock = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/settings");
      setOrderLock(res.data.orderLock);
    } catch (err) {
      console.log("Order lock load failed", err);
    }
  };
  loadOrderLock();
}, []);

useEffect(() => {
  const fetchAnnouncement = async () => {
    try {
      const res = await fetch("https://api.speedfollowinfo.com/api/settings");
      const data = await res.json();
      if (data?.announcement) {
  setAnnouncement(data.announcement);
  localStorage.setItem("announcement", data.announcement);
}
    } catch (err) {
      console.error("❌ Failed to load announcement:", err);
    }
  };
  fetchAnnouncement();
  const interval = setInterval(fetchAnnouncement, 60000); // প্রতি ১ মিনিটে আপডেট হবে
  return () => clearInterval(interval);
}, []);

  // total price derived value (handles lockQuantity)
const totalPrice = selectedService
  ? selectedService.lockQuantity
    ? Number(selectedService.price)
    : (Number(quantity || 0) * Number(selectedService.price || 0)) / 1000
  : 0;
// 🔒 Low balance shortage calculation
const balanceShortage =
  selectedService && Number(user.balance) < Number(totalPrice)
    ? (Number(totalPrice) - Number(user.balance)).toFixed(2)
    : 0;
// ❌ remove popup auto-trigger from useEffect
useEffect(() => {
  if (user?.balance === undefined) {
    setInsufficient(false);
    return;
  }

  if (totalPrice > 0 && Number(user.balance) < Number(totalPrice)) {
    setInsufficient(true);
  } else {
    setInsufficient(false);
  }
}, [user, totalPrice]);
 // Initial token/user validation and load basic values
useEffect(() => {
  const token = localStorage.getItem("token");
  const rawUser = localStorage.getItem("user");

  if (!token || !rawUser) {
    // if not logged in, redirect to login immediately
    window.location.href = "/login";
    return;
  }

  try {
    const stored = JSON.parse(rawUser); // local stored user

    const previousBalance =
      stored?.balance !== undefined && stored?.balance !== null
        ? Number(stored.balance)
        : null;

    const previousSpend =
      stored?.spend !== undefined && stored?.spend !== null
        ? Number(stored.spend)
        : null;

    setUsername(stored.username || "");

    // ❗Important: keep old balance if backend sends undefined
    setBalance(previousBalance);

    // ❗Same for spend
    setSpend(previousSpend);

    setUser(stored);
  } catch (err) {
    console.warn("Failed to parse stored user, clearing and redirecting", err);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  }
}, []);
  useEffect(() => {
    fetchOrders();
    fetchUser();
  }, []);

  // 🔔 FIREBASE FCM TOKEN → BACKEND SAVE
useEffect(() => {
  const saveFcmToken = async () => {
    try {
      const authToken = localStorage.getItem("token");
      if (!authToken) return;

      // 🔥 Firebase থেকে permission + FCM token
      const fcmToken = await requestNotificationPermission();
      console.log("📦 TOKEN RECEIVED IN DASHBOARD:", fcmToken);
      if (!fcmToken) return;

      // 🔐 Backend এ save করো
      await axios.post(
        "https://api.speedfollowinfo.com/api/user/save-fcm-token",
        { token: fcmToken },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      console.log("✅ FCM token saved successfully");
    } catch (err) {
      console.error("❌ FCM token save failed:", err);
    }
  };

  saveFcmToken();
}, []);
  // close profile dropdown when click outside
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close notification panel when clicked outside
useEffect(() => {
  const handleClickOutside = (e) => {
    if (
      notifRef.current &&
      !notifRef.current.contains(e.target) &&
      !e.target.closest("#notif-btn") // ignore notification button
    ) {
      setShowNotifications(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

// 🔔 Fetch Notifications From Backend + Play Sound
const [lastCount, setLastCount] = useState(0);

useEffect(() => {
  const fetchUserNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await axios.get("https://api.speedfollowinfo.com/api/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newData = res.data.notifications || [];

      // 🔥 সাউন্ড বাজবে শুধু তখনই যখন নতুন নোটিফিকেশন আসবে
      if (newData.length > lastCount) {
        notificationSound.currentTime = 0;   // রিসেট
        notificationSound.play().catch(() => {});
      }

      setNotifications(newData);
      setLastCount(newData.length);

    } catch (err) {
      console.error("❌ Failed to fetch notifications:", err.message);
    }
  };

  fetchUserNotifications();
  const interval = setInterval(fetchUserNotifications, 30000);

  return () => clearInterval(interval);
}, [lastCount]);
  // ============================
  // Backend fetch helpers (kept same names so rest of file uses them)
  // ============================
  const fetchOrders = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/orders");
      setTotalOrders(res.data?.orders?.length || 0);
      localStorage.setItem(
  "orders",
  JSON.stringify(res.data?.orders || [])
);
    } catch (err) {
      setTotalOrders(0);
    }
  };

 // REPLACE your existing fetchUser with this safe, merge-aware version
const fetchUser = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return;

    const res = await axios.get("https://api.speedfollowinfo.com/api/user", {
      headers: { Authorization: `Bearer ${token}` },
    });

    const backendUser = res.data?.user || {};

    // Merge with existing local stored user to avoid losing fields (like balance)
    let stored = {};
    try {
      stored = JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      stored = {};
    }

    // Prefer backend values when they exist, otherwise fall back to stored values
    const merged = {
      ...stored,
      ...backendUser,
      // ensure numeric values stay numeric; if backend missing, keep stored numeric
      balance:
        backendUser.balance !== undefined && backendUser.balance !== null
          ? Number(backendUser.balance)
          : stored.balance !== undefined && stored.balance !== null
          ? Number(stored.balance)
          : 0,
      spend:
        backendUser.spend !== undefined && backendUser.spend !== null
          ? Number(backendUser.spend)
          : stored.spend !== undefined && stored.spend !== null
          ? Number(stored.spend)
          : 0,
    };

    // 🚫 Suspended user handling (apply to merged user, but backendUser likely carries status)
    if (merged.status === "suspended") {
      localStorage.setItem("user", JSON.stringify(merged));
      navigate("/account-suspended", { replace: true });
      return;
    }

    // ✅ Update states with merged safe data
    setUsername(merged.username || "");
    setBalance(
  merged.balance === null || merged.balance === undefined
    ? null
    : Number(merged.balance)
);

setSpend(
  merged.spend === null || merged.spend === undefined
    ? null
    : Number(merged.spend)
);
    setUser(merged);

    // ✅ Persist merged user back to localStorage
    localStorage.setItem("user", JSON.stringify(merged));

    // 🔔 Notify other pages/components
    window.dispatchEvent(new Event("userUpdated"));
  } catch (err) {
    console.error("Fetch user failed:", err?.response?.data || err?.message);

    if (
      err?.response?.status === 403 &&
      err?.response?.data?.message?.toLowerCase()?.includes("suspended")
    ) {
      navigate("/account-suspended", { replace: true });
      return;
    }

    if (err?.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      navigate("/login", { replace: true });
    }
  } finally {
    setLoadingUser(false);
  }
};
// ✅ Secure User Logout (only user keys; DO NOT clear admin keys)
const handleLogout = () => {
  try {
    // Remove only user-related keys
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // Some apps cache session in sessionStorage
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");

    // Optional: clear client caches (non-blocking)
    if ("caches" in window) {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }

    // prevent back navigation to protected pages
    try {
      window.history.pushState(null, "", "/login");
    } catch (e) {
      console.warn("History cleanup failed:", e);
    }

    // Safe redirect to login
    setTimeout(() => {
      window.location.replace("/login");
    }, 150);
  } catch (err) {
    console.error("Logout error:", err);
    alert("⚠️ Logout failed, please try again.");
  }
};
 // --- (rest of your Dashboard code follows after line ~232; do not close the function here)
  // ==================== Categories & Services ====================

// 🧩 Default Local Services (তোমার আগেরগুলো অপরিবর্তিত থাকবে)

// 🧠 Dynamic services state
const [dynamicServices, setDynamicServices] = useState([]);

// ✅ Fetch dynamic services from backend
useEffect(() => {
  axios
    .get("https://api.speedfollowinfo.com/api/services")
    .then((res) => {
      if (res.data.success) setDynamicServices(res.data.services);
    })
    .catch((err) => console.error("⚠️ Failed to fetch services:", err));
}, []);

// ✅ Merge both (local + backend)
const servicesData = useMemo(() => {
  const merged = {};
  dynamicServices.forEach((srv) => {
    const cat = srv.category?.trim();
    if (!cat) return; // ❌ category ফাঁকা থাকলে skip করো
    if (!merged[cat]) merged[cat] = [];
    merged[cat].push({
  name: srv.name,
  price: Number(srv.price) || 0,
  lockQuantity: !!srv.lockQuantity,
  placeholder: srv.placeholder || "Enter details here...",
  description: srv.description || "",   // <-- ✨ নতুন লাইন
  isActive: srv.isActive ?? true,
});
  });
  return merged;
}, [dynamicServices]);

// ✅ All categories list
const categories = Object.keys(servicesData);

// 🧠 Dynamic backend + local service merge
const [servicesMap, setServicesMap] = useState({});

useEffect(() => {
  const fetchServices = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/services");
      if (res.data?.services && Array.isArray(res.data.services)) {
        const map = {};
        res.data.services.forEach((s) => {
          const cat = s.category || "Others";
          if (!map[cat]) map[cat] = [];
         map[cat].push({
  name: s.name,
  price: Number(s.price) || 0,
  lockQuantity: !!s.lockQuantity,
  placeholder: s.placeholder || "",
  description: s.description || "",   // <-- ✨ নতুন লাইন
  isActive: s.isActive ?? true,
});
        });
        setServicesMap((prev) => ({ ...prev, ...map }));
      }
    } catch (err) {
      console.warn("⚠️ Failed to load dynamic services:", err.message);
    }
  };
  fetchServices();

  // ✅ Auto reload services when new one is added from Admin panel
  const refreshServices = () => {
    axios
      .get("https://api.speedfollowinfo.com/api/services")
      .then((res) => {
        if (res.data?.services && Array.isArray(res.data.services)) {
          const map = {};
          res.data.services.forEach((s) => {
            const cat = s.category || "Others";
            if (!map[cat]) map[cat] = [];
            map[cat].push({
              name: s.name,
              price: Number(s.price) || 0,
              lockQuantity: !!s.lockQuantity,
              placeholder: s.placeholder || "",
              isActive: s.isActive ?? true, // ✅ added here as well
            });
          });
          setServicesMap((prev) => ({ ...prev, ...map }));
          // 🆕 Popup when new service added
          const toast = document.createElement("div");
          toast.textContent = "🆕 New service added!";
          toast.style.position = "fixed";
          toast.style.top = "20px";
          toast.style.right = "20px";
          toast.style.background = "#00e6a8";
          toast.style.color = "#0b0f1a";
          toast.style.padding = "10px 18px";
          toast.style.fontWeight = "600";
          toast.style.borderRadius = "8px";
          toast.style.boxShadow = "0 0 10px rgba(0,0,0,0.5)";
          toast.style.zIndex = "9999";
          document.body.appendChild(toast);
          setTimeout(() => toast.remove(), 2000);
        }
      })
      .catch((err) =>
        console.error("⚠️ Failed to auto refresh services:", err)
      );
  };

  window.addEventListener("serviceAdded", refreshServices);
  return () => window.removeEventListener("serviceAdded", refreshServices);
}, []);

// ✅ Category list ready
const handleServiceClick = (service) => {
  setSelectedService(service);
  setQuantity(service.lockQuantity ? 1 : "");
};

// ===============================
// 🚀 QUICK CATEGORY SELECT (FIXED & STABLE)
// ===============================
const handleQuickCategorySelect = (iconKey) => {
  if (!iconKey || !categories.length) return;

  // 🔄 reset states
  setNoServiceMessage("");
  setMatchedCategories([]);
  setSelectedService(null);

  const aliases = keywordAliases[iconKey] || [iconKey];

  // 🔍 smart match (emoji / symbol safe)
  const matches = categories.filter((cat) => {
    const normalizedCat = normalizeText(cat);
    return aliases.some((key) =>
      normalizedCat.includes(normalizeText(key))
    );
  });

  // ❌ No category found
  if (matches.length === 0) {
    setSelectedCategory("");
    setShowCategoryDropdown(false);
    setNoServiceMessage(
      "🚫 এই সার্ভিস টি নিয়ে কাজ চলতেছে, খুব দ্রুত সার্ভিস এড করা হবে"
    );

    categorySectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    return;
  }

  // ✅ Single match → auto open category
  if (matches.length === 1) {
    setSelectedCategory(matches[0]);
    setShowCategoryDropdown(false);

    categorySectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    return;
  }

  // 📂 Multiple match → show dropdown list
  setSelectedCategory("");
  setMatchedCategories(matches);
  setShowCategoryDropdown(true);

  categorySectionRef.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

// ===============================
// ✅ ORDER SUBMIT HANDLER (STABLE)
// ===============================
const handleOrderSubmit = async (e) => {
  e.preventDefault();

  // 🔒 Extra safety
  if (Number(user.balance) < Number(totalPrice)) return;
  if (isOrdering) return;

  setIsOrdering(true);

  try {
    const token = localStorage.getItem("token");
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (!token || !storedUser?.username) {
      alert("⚠️ Please login again!");
      navigate("/login");
      return;
    }

    // 💰 Low balance popup
    if (storedUser.balance < totalPrice) {
      setLowBalancePopup({
        balance: storedUser.balance,
        needed: totalPrice,
        shortage: (totalPrice - storedUser.balance).toFixed(2),
      });

      fetchUser();
      fetchOrders();
      setIsOrdering(false);
      return;
    }

    // 🛒 Submit order
    await axios.post(
  "https://api.speedfollowinfo.com/api/orders",
  {
    service: selectedService.name,
    categoryType: selectedCategory,
    price: totalPrice,
    info: accountUrl,
    quantity: selectedService.lockQuantity ? 1 : Number(quantity),
  },
  {
    headers: { Authorization: `Bearer ${token}` },
  }
);

    // 🟢 Success popup
    setOrderPopup({
      category: selectedCategory,
      service: selectedService.name,
      price: totalPrice,
      status: "Pending",
    });

    // 🔄 Reset form
    setAccountUrl("");
    setSelectedService(null);
    setSelectedCategory("");
    setQuantity(1);

    await fetchOrders();
    await fetchUser();
    window.dispatchEvent(new Event("userUpdated"));

  } catch (err) {
    console.error("Order Error:", err);
    alert("❌ Something went wrong.");
  } finally {
    setIsOrdering(false);
  }
};

  const panelTotalOrders = 1968 + totalOrders;
  const card = darkMode ? "bg-[#161c34] text-white" : "bg-white text-black";
  const surface = darkMode ? "bg-[#1b2142] text-white" : "bg-gray-100 text-black";
  const field = darkMode
    ? "bg-[#0f1530] border border-[#30365f] text-white"
    : "bg-white border border-gray-300 text-black";

  return (
  <div className="flex bg-[#0b0f1a] text-white min-h-screen transition-all duration-500 ease-in-out">

{/* 🔥 LOW BALANCE POPUP */}
{lowBalancePopup && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">

    <div className="relative w-[90%] max-w-md p-6 rounded-2xl shadow-[0_0_25px_rgba(255,0,0,0.4)] 
      bg-gradient-to-br from-[#1a0f0f] to-[#2b1b1b] border border-red-400/40 animate-scaleIn">

      {/* Glow */}
      <div className="absolute inset-0 rounded-2xl border border-red-500/30 
        animate-pulse pointer-events-none"></div>

      <h2 className="text-2xl font-extrabold text-red-400 text-center mb-4 drop-shadow-lg">
        ⚠️ ব্যালেন্স পর্যাপ্ত নয়!
      </h2>

      <div className="space-y-3 text-gray-200 text-lg font-semibold">
        
        <p>💰 আপনার ব্যালেন্স:  
          <span className="text-yellow-300 ml-1">
            ৳{lowBalancePopup.balance}
          </span>
        </p>

        <p>🧾 অর্ডারের দাম:
          <span className="text-yellow-300 ml-1">
            ৳{lowBalancePopup.needed}
          </span>
        </p>

        <p>
          🔓 আরও প্রয়োজন:  
          <span className="text-green-300 ml-1">
            ৳{lowBalancePopup.shortage}
          </span>
        </p>

      </div>

      {/* Buttons */}
      <div className="mt-6 flex justify-between gap-4">

        {/* Add Funds */}
        <button
  onClick={() => {
    setLowBalancePopup(null);
    fetchUser();  // ← এটাতেও refresh দাও
    navigate("/addfunds");
  }}
  className="w-1/2 py-3 rounded-xl bg-green-500 text-black font-bold 
  hover:bg-green-400 shadow-lg transition"
>
  Add Fund
</button>

        {/* OK */}
        <button
  onClick={() => {
    setLowBalancePopup(null);
    fetchUser(); // 🔥 Auto-refresh balance + UI
  }}
  className="w-1/2 py-3 rounded-xl bg-gray-600 text-white font-bold 
  hover:bg-gray-500 shadow-lg transition"
>
  OK
</button>

      </div>
    </div>

    <style>{`
      @keyframes scaleIn {
        0% { transform: scale(0.7); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      .animate-scaleIn { animation: scaleIn 0.25s ease-out; }
    `}</style>
  </div>
)}

    {/* 🟢 Order Success PopUp (REPLACE THIS WHOLE BLOCK) */}
{orderPopup && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">

    <div className="relative w-[90%] max-w-md p-6 rounded-2xl shadow-[0_0_25px_rgba(0,255,120,0.5)] 
      bg-gradient-to-br from-[#0d1117] to-[#111827] border border-green-400/40 animate-scaleIn">

      {/* Glow Ring */}
      <div className="absolute inset-0 rounded-2xl border border-green-500/30 
        animate-pulse pointer-events-none"></div>

      {/* Title */}
      <h2 className="text-3xl font-extrabold text-green-400 text-center mb-4 drop-shadow-lg">
        🎉 Order Successful!
      </h2>

      {/* Details */}
      <div className="space-y-2 text-gray-200 text-lg font-semibold">
        <p>📦 <span className="text-green-300">Category:</span> {orderPopup.category}</p>

        <p>🚀 <span className="text-green-300">Service:</span> {orderPopup.service}</p>

        <p>💰 <span className="text-green-300">Price:</span> ৳{orderPopup.price}</p>

        <p>
          ⏳ <span className="text-yellow-300">Status:</span>  
          <span className="text-yellow-400 ml-1">Pending...</span>
        </p>
      </div>

      {/* Button */}
      <button
        onClick={() => setOrderPopup(null)}
        className="mt-6 w-full py-3 rounded-xl text-black font-bold text-lg
          bg-gradient-to-r from-green-400 to-green-300 hover:opacity-90 
          shadow-[0_0_10px_rgba(0,255,120,0.6)] transition-all"
      >
        OK ✓
      </button>
    </div>

    {/* Animation */}
    <style>{`
      @keyframes scaleIn {
        0% { transform: scale(0.7); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      .animate-scaleIn { animation: scaleIn 0.25s ease-out; }
    `}</style>
  </div>
)}
    <Sidebar onToggle={setSidebarOpen} />

    {/* Dashboard Content */}
    <div
      className={`transition-all duration-500 ease-in-out ${
        sidebarOpen ? "ml-64 w-[calc(100%-16rem)]" : "ml-0 w-full"
      }`}
    >

      {/* Header Section */}
      <div className="flex justify-between items-center px-4 pt-20">
        <h1 className="text-3xl font-bold text-white"> </h1>

        {/* 🟡 Announcement Bar */}
        {announcement && (
  <div
  className="
    announcement-bar
    bg-[#111827]
    border border-[#6c63ff]/40
    rounded-xl
    shadow-lg
    px-3
    py-1
    mt-3
    ml-0
    max-w-[60%]
    text-left
    overflow-hidden
    flex
  "
>
    <marquee
      behavior="scroll"
      direction="left"
      scrollamount="4"
      className="
        text-[#facc15]
        font-semibold
        whitespace-nowrap
        text-sm
        md:text-base
      "
    >
      📢 {announcement}
    </marquee>
  </div>
)}
  <div className="flex items-center gap-3 relative" ref={profileRef}>

  {/* 🔔 Notification Button */}
<button
  id="notif-btn"
  onClick={(e) => {
    e.stopPropagation();
    setShowNotifications(!showNotifications);

    // mark all as read when opened
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true }))
    );
  }}
  className="relative p-2 rounded-full bg-[#2a2f45] hover:bg-[#363c57] text-white transition shadow-lg"
>
  <img
    src="https://cdn-icons-png.flaticon.com/512/1827/1827310.png"
    alt="Notification"
    className="w-5 h-5"
  />

  {/* 🔴 Unread Red Dot */}
  {notifications.some(n => n.read === false) && (
    <span className="absolute top-0 right-0 w-3 h-3 bg-red-600 
                     rounded-full shadow-[0_0_10px_#ff0000] animate-ping">
    </span>
  )}
</button>

{/* 🔔 Notification Panel */}
{showNotifications && (
  <div 
    ref={notifRef}
    className="absolute right-16 top-14 w-72 bg-[#1a1f33] border border-white/20 
               shadow-xl rounded-xl p-4 z-50 animate-scaleIn"
  >
    <h3 className="text-white font-bold mb-3 text-lg">Notifications 🔔</h3>

    {notifications.length === 0 ? (
      <p className="text-gray-400 text-sm text-center">No notifications</p>
    ) : (
      <ul className="space-y-3 max-h-60 overflow-y-auto">
        {notifications.map((n) => (
          <li 
            key={n._id}
            className="bg-[#232a45] text-gray-200 p-3 rounded-lg border border-white/10"
          >
            {n.message}
          </li>
        ))}
      </ul>
    )}

    {/* 🧹 Clear All Button */}
    <button
      onClick={clearNotifications}
      className="mt-4 w-full py-2 bg-red-600 hover:bg-red-700 text-white 
                 font-semibold rounded-lg transition"
    >
      Clear All
    </button>
  </div>
)}
  {/* 🌗 Dark Mode Toggle */}
  <button
    onClick={() => setDarkMode(!darkMode)}
    className="p-2 rounded-full bg-[#2a2f45] hover:bg-[#363c57] text-white transition shadow-lg"
  >
    {darkMode ? <Sun size={18} /> : <Moon size={18} />}
  </button>

  {/* 👤 Profile Button */}
<button
  onClick={() => setShowProfileMenu((s) => !s)}
  className="relative flex items-center justify-center w-10 h-10 rounded-full 
             bg-[#1f2437] hover:bg-[#2b3150] transition shadow-lg 
             border border-[#3b4268]"
>
  <img
    src={siteLogo}
    alt="Speed Follow Logo"
    className="w-full h-full object-cover rounded-full"
  />

  {/* Online Dot (Premium UI) */}
  <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 
                  border border-[#1f2437] shadow-lg"></span>
</button>

    {/* 🧾 Profile Dropdown */}
{showProfileMenu && (
  <div
    className="absolute right-0 top-14 w-72 rounded-2xl p-4 z-50 
    bg-gradient-to-br from-[#0e1220] to-[#111827]
    shadow-[0_0_25px_rgba(0,150,255,0.35)]
    border border-[#1f2937]/60 backdrop-blur-xl animate-scaleIn"
  >

    {/* ================= USERNAME + BALANCE ================ */}
    <div className="text-center pb-4 border-b border-white/10">
      <div className="flex justify-center items-center gap-2">
        <span className="text-xl font-bold text-white drop-shadow-lg">
          {username}
        </span>

        {/* Blue Verified Badge */}
        <span className="w-5 h-5 flex items-center justify-center rounded-full 
        bg-blue-500 shadow-[0_0_8px_#3b82f6]">
          <svg xmlns="http://www.w3.org/2000/svg" 
            fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="3"
            className="w-3 h-3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </div>

      <div className="text-green-400 mt-1 font-semibold text-lg">
  {balance === null ? "Loading " : `৳ ${balance.toFixed(2)}`}
</div>
    </div>

    {/* ================= MENU BUTTONS ================= */}
    <div className="mt-4 space-y-3">

      {/* Account */}
      <button
        onClick={() => { navigate('/account'); setShowProfileMenu(false); }}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl 
        bg-white/5 hover:bg-white/10 transition 
        border border-white/10 shadow-sm"
      >
        <img src="https://cdn-icons-png.flaticon.com/512/9131/9131529.png"
          className="w-6 opacity-90" />
        <span className="text-white font-medium text-md">Account</span>
      </button>

      {/* Add Funds */}
      <button
        onClick={() => { navigate('/addfunds'); setShowProfileMenu(false); }}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl 
        bg-white/5 hover:bg-white/10 transition 
        border border-white/10 shadow-sm"
      >
        <img src="https://cdn-icons-png.flaticon.com/512/3135/3135706.png"
          className="w-6" />
        <span className="text-white font-medium text-md">Add Funds</span>
      </button>

      {/* Terms */}
      <button
        onClick={() => { navigate('/terms'); setShowProfileMenu(false); }}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl 
        bg-white/5 hover:bg-white/10 transition 
        border border-white/10 shadow-sm"
      >
        <img src="https://cdn-icons-png.flaticon.com/512/4436/4436481.png"
          className="w-6" />
        <span className="text-white font-medium text-md">Terms & Conditions</span>
      </button>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl 
        bg-red-500/10 hover:bg-red-500/20 transition 
        border border-red-500/30 shadow-sm"
      >
        <img src="https://cdn-icons-png.flaticon.com/512/1828/1828490.png"
          className="w-6" />
        <span className="text-red-400 font-medium text-md">Logout</span>
      </button>

  </div>
  </div>
)}
  </div>
      </div>
      {/* ⭐ PREMIUM VIP STATS SECTION */}
<div className="px-4 mt-8 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5">

  {/* CARD FUNCTION */}
{[
  {
    title: username ? username : "Loading...",
    subtitle: "Username",
    icon: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
  },
  {
    title:
      balance !== null && balance !== undefined
        ? `৳${balance.toFixed(2)}`
        : "Loading...",
    subtitle: "Balance",
    icon: "https://cdn-icons-png.flaticon.com/512/2164/2164524.png"
  },
  {
    title:
      spend !== null && spend !== undefined
        ? `৳${spend.toFixed(2)}`
        : "Loading...",
    subtitle: "Total Spend",
    icon: "https://cdn-icons-png.flaticon.com/512/1170/1170678.png"
  },
  {
    title:
      totalOrders !== null && totalOrders !== undefined
        ? panelTotalOrders
        : "Loading...",
    subtitle: "Total Orders",
    icon: "https://cdn-icons-png.flaticon.com/512/2593/2593441.png"
  }
].map((item, i) => (
  <div
    key={i}
    className="relative overflow-hidden bg-white/5 
    backdrop-blur-xl rounded-2xl p-5 shadow-[0_0_20px_rgba(0,255,200,0.2)] 
    border border-white/10 hover:shadow-[0_0_35px_rgba(0,255,200,0.35)] 
    hover:-translate-y-1 transition-all duration-300"
  >

      {/* Glow Layer */}
      <div className="absolute inset-0 bg-gradient-to-br 
        from-[#00ffc3]/10 to-[#0099ff]/10 rounded-2xl pointer-events-none" />

      {/* Card Content */}
      <div className="relative z-10 flex items-center gap-4">
       <img
         src={item.icon}
         alt=""
         className="
           w-14 h-14
           rounded-full
           object-cover
           drop-shadow-[0_0_10px_rgba(0,255,200,0.4)]
         "
       />
        <div>
          <h3 className="text-xl font-bold text-white drop-shadow-sm">
            {item.title}
          </h3>

          <p className="text-sm text-gray-300 tracking-wide">
            {item.subtitle}
          </p>
        </div>
      </div>
    </div>
  ))}
</div>
{/* 🔥 PREMIUM SERVICE ICON CARDS */}
<div className="px-4 mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
 {[
  { name: "Facebook", icon: "facebook", keyword: "facebook" },
  { name: "YouTube", icon: "youtube", keyword: "youtube" },
  { name: "TikTok", icon: "tiktok", keyword: "tiktok" },
  { name: "Instagram", icon: "instagram", keyword: "instagram" },
  { name: "Telegram", icon: "telegram", keyword: "telegram" },
  { name: "Online Payment", icon: "payment", keyword: "payment" },
  { name: "SIM Service", icon: "sim", keyword: "sim" },
  { name: "FF Diamond", icon: "diamond", keyword: "diamond" },
  { name: "KYC Service", icon: "kyc", keyword: "kyc" },
  { name: "Verified", icon: "verify", keyword: "verify" },
].map((item, i) => (
  <div
    key={i}
    onClick={() => handleQuickCategorySelect(item.keyword)}
    className="premium-service-card relative overflow-hidden cursor-pointer
      backdrop-blur-xl rounded-2xl p-5
      border border-white/10
      shadow-[0_0_20px_rgba(0,255,255,0.18)]
      hover:shadow-[0_0_35px_rgba(0,255,255,0.35)]
      hover:-translate-y-1 transition-all duration-300
      flex flex-col items-center justify-center gap-3"
  >
    <div className="absolute inset-0 bg-gradient-to-br 
      from-[#00eaff]/10 to-[#0066ff]/10 pointer-events-none"
    />

    <PremiumIcon type={item.icon} />

    <span className="text-sm font-semibold text-gray-200 text-center">
      {item.name}
    </span>
  </div>
))}
</div>
{/* Category + Note Section */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4 mt-6">
  {/* Category Section */}
<div
  ref={categorySectionRef}
  className={`${surface} p-4 rounded-xl shadow-xl`}
  id="new-order-section"
>
  <h2 className="text-xl font-bold mb-4">🛍️ Select Service 👇💯</h2>

  {/* Custom Category Dropdown */}
  <div className="relative">
    <div className="relative mb-4">
      <button
        onClick={() => {
          if (!orderLock) setShowCategoryDropdown(!showCategoryDropdown);
        }}
        disabled={orderLock}
        className={`w-full p-3 rounded border text-center font-bold relative overflow-hidden
          ${
            orderLock
              ? "text-white cursor-not-allowed fire-btn"
              : "bg-[#0f1530] text-white border-[#30365f]"
          }`}
      >
        {orderLock
          ? "🔥 Order System Coming Soon 💥"
          : selectedCategory || "-- Select Category --"}
        <span className="ml-2">▼</span>
      </button>

      {/* 🔥 Fire Wave Border Animation */}
      {orderLock && (
        <div className="absolute inset-0 rounded-lg pointer-events-none fire-wave"></div>
      )}
    </div>

    {/* 🔴 NO SERVICE MESSAGE (এইটাই তুমি জিজ্ঞেস করছিলে) */}
    {noServiceMessage && (
      <div className="mt-3 text-center text-red-400 font-semibold animate-pulse">
        {noServiceMessage}
      </div>
    )}

    {/* Dropdown */}
    {showCategoryDropdown && (
      <ul
        className="absolute z-50 w-full max-h-[300px] overflow-y-auto bg-[#111727] border border-[#30365f] rounded-lg shadow-lg mt-1 animate-fadeIn"
        style={{ top: "100%", left: 0 }}
      >
        {(matchedCategories.length > 0
          ? matchedCategories
          : categories
        ).map((cat, i) => (
          <li
            key={i}
            onClick={() => {
              setSelectedCategory(cat);
              setMatchedCategories([]);
              setSelectedService(null);
              setShowCategoryDropdown(false);
              setNoServiceMessage(""); // ✅ ক্লিয়ার করে দেবে
            }}
            className={`px-4 py-3 cursor-pointer transition ${
              selectedCategory === cat
                ? "bg-[#4f46e5] text-white"
                : "text-gray-200 hover:bg-[#1f2750]"
            }`}
          >
            {cat}
          </li>
        ))}
      </ul>
    )}
  </div>

  {/* ✅ Service List */}
  {selectedCategory && (
    <ul className="space-y-3 mb-6 max-h-[300px] overflow-y-auto">
      {servicesData[selectedCategory] &&
      servicesData[selectedCategory].length > 0 ? (
        (selectedService
          ? [selectedService]
          : servicesData[selectedCategory]
        ).map((service, index) => (
          <li
            key={index}
            onClick={() => handleServiceClick(service)}
            className={`px-4 py-3 rounded cursor-pointer transition ${
              selectedService?.name === service.name
                ? "bg-green-600 text-white"
                : darkMode
                ? "bg-[#161c34] hover:bg-[#1f2750] text-white"
                : "bg-gray-200 hover:bg-gray-300 text-black"
            }`}
          >
            <div className="flex flex-col">
  <span className="font-semibold flex items-center justify-between">
    {service.name}
    {!service.isActive && (
      <span className="text-red-500 text-xs ml-2 font-semibold animate-pulse">
        ❌ নতুন অর্ডার বন্ধ আছে
      </span>
    )}
  </span>

 <span className="text-sm opacity-80 flex items-center gap-1">
  <span className="text-gray-300 font-bold">৳</span>
  <span>{service.price}</span>
  {!service.lockQuantity && <span> (per 1000)</span>}
</span>
</div>
          </li>
        ))
      ) : (
        <li className="p-4 text-center text-gray-400 italic border border-gray-700 rounded-lg">
          🚫 No services available in this category.
        </li>
      )}
    </ul>
  )}

{/* ✅ Order Form */}
{selectedService && (
  <form onSubmit={handleOrderSubmit} className="space-y-4">

    {/* 📝 Service Description */}
    {selectedService.description && (
      <div className="p-3 bg-[#111727] border border-[#30365f] text-gray-300 rounded-lg text-sm">
        <h4 className="text-[#f2a900] font-bold mb-1">📝 Service Details:</h4>
        <p>{selectedService.description}</p>
      </div>
    )}

    {/* Account URL / Details */}
    <textarea
      value={accountUrl}
      onChange={(e) => setAccountUrl(e.target.value)}
      placeholder={
        selectedService.lockQuantity
          ? selectedService.placeholder
          : "Paste Urls Link"
      }
      className={`w-full p-3 rounded min-h-[100px] ${field}`}
      required
      disabled={selectedService?.isActive === false}
    />

    {/* Quantity */}
    <input
      type="number"
      value={quantity}
      onChange={(e) => setQuantity(e.target.value)}
      disabled={
        selectedService.lockQuantity || selectedService?.isActive === false
      }
      placeholder="Quantity"
      className={`w-full p-3 rounded ${field}`}
      required
    />
    <div className="text-lg font-bold">
  Total Price: ৳{" "}
  {selectedService.lockQuantity
    ? selectedService.price
    : (Number(quantity) * selectedService.price) / 1000}
</div>

    {insufficient && (
      <div className="text-red-500 font-semibold mb-3 text-center">
        ❌ Insufficient Balance! Please Add Funds.
      </div>
    )}

    {/* ✅ Order Button with Low Balance + Disable Logic */}
{selectedService?.isActive ? (
  <button
    type="submit"
    disabled={
      isOrdering ||
      Number(user.balance) < Number(totalPrice)
    }
    className={`w-full p-3 rounded font-semibold transition-all
      ${
        Number(user.balance) < Number(totalPrice)
          ? "bg-gray-600 text-gray-300 cursor-not-allowed"
          : isOrdering
          ? "bg-gray-500 cursor-not-allowed text-white"
          : "bg-green-600 hover:bg-green-500 text-white"
      }
    `}
  >
    {Number(user.balance) < Number(totalPrice)
      ? `Low Balance • Add ৳${balanceShortage}`
      : isOrdering
      ? "Processing..."
      : "🛒 Order Now"}
  </button>
) : (
  <div className="w-full bg-red-700 text-white p-3 rounded font-semibold text-center animate-pulse">
    ❌ নতুন অর্ডার বন্ধ আছে
  </div>
)}
  </form>
)}
</div>
{/* 🔰 Premium Note Section */}
<div
  className={`p-5 rounded-xl shadow-xl border 
    ${darkMode 
      ? "bg-[#1a1f35] text-gray-200 border-[#2a3150]" 
      : "bg-[#eef1ff] text-gray-900 border-[#cfd5f7]"
    }`}
>

  <h2 className="text-lg font-bold mb-3 tracking-wide
                 text-[#7dd3fc] drop-shadow">
    IMPORTANT NOTE ⚠️
  </h2>

  <p className="mb-3 leading-relaxed">
    ‎বি দ্রঃ আমরা সব সময় কাজ রানিং এবং দ্রুত করার চেষ্টা করি 😊  
    যে সব সার্ভিস এ <b>Order Button</b> থাকবে ঐসব অর্ডার করবেন।  
    অবশ্যই <b>সময় নিয়ে</b> কাজ দিবেন।  ✅
  </p>

  <p className="mb-3 leading-relaxed">
    ‎Note: We always try to keep all services running and fast √  
    Order only those services that have an <b>Order Button</b>  
    and please work with proper time. ✪
  </p>

  <hr className={`${darkMode ? "border-[#2e3655]" : "border-[#dbe1ff]"} my-3`} />

  <p className="leading-relaxed">
    ‎গুরুত্বপূর্ণ বিষয় — কাজ শেষ হলে  
    <span className="text-red-400 font-semibold"> সমস্ত হিস্টোরি / মেসেজ ডিলেট </span> 
    করে রাখবেন ⛔  
  </p>

  <p className="mt-1 leading-relaxed">
    ‎Important point – At the end of work,  
    <span className="text-red-500 font-semibold">delete all history</span> ⛔
  </p>

</div>
</div>
{/* 🌐 Telegram Floating Button (Left Side, Pulse Effect 💙) */}
<a
  href="https://t.me/Prithibi144" // এখানে তোমার Telegram username দাও
  target="_blank"
  rel="noreferrer"
  className="fixed left-5 bottom-5 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110 animate-pulse-telegram"
  style={{
    backgroundColor: "#0088cc",
    boxShadow: "0 4px 15px rgba(0, 136, 204, 0.5)",
  }}
  title="Chat on Telegram"
>
  <img
    src="https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
    alt="Telegram"
    className="w-8 h-8"
  />
</a>

{/* 💚 WhatsApp Floating Button (Right Side, Pulse Effect) */}
<a
 href="https://wa.me/8801635315625"
  target="_blank"
  rel="noreferrer"
  className="fixed right-5 bottom-5 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110 animate-pulse-whatsapp"
  style={{
    backgroundColor: "#25D366",
    boxShadow: "0 4px 15px rgba(37, 211, 102, 0.5)",
  }}
  title="Chat on WhatsApp"
>
  <img
    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
    alt="WhatsApp"
    className="w-8 h-8"
  />
</a>

{/* 🌈 Floating + Premium Icon Animations */}
<style jsx>{`
/* ===============================
   🔵 WHATSAPP FLOATING PULSE
================================ */
@keyframes pulse-whatsapp {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
}
.animate-pulse-whatsapp {
  animation: pulse-whatsapp 2.5s infinite;
}

/* ===============================
   🔵 TELEGRAM FLOATING PULSE
================================ */
@keyframes pulse-telegram {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 136, 204, 0.6);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 136, 204, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 136, 204, 0);
  }
}
.animate-pulse-telegram {
  animation: pulse-telegram 2.5s infinite;
}

/* ===============================
   🌟 TELEGRAM PREMIUM ICON STYLE
   (NO BLACK BACKGROUND)
================================ */
.premium-icon-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: radial-gradient(
    circle at top,
    rgba(0, 200, 255, 0.25),
    rgba(0, 60, 120, 0.35)
  );
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.premium-icon-img {
  width: 32px;
  height: 32px;
  z-index: 2;
  filter: drop-shadow(0 0 7px rgba(0, 220, 255, 0.9));
}

/* Rotating Glow Ring */
.premium-glow {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    rgba(0,255,255,0.0),
    rgba(0,255,255,0.9),
    rgba(0,140,255,0.9),
    rgba(0,255,255,0.0)
  );
  animation: premiumSpin 3s linear infinite;
  filter: blur(9px);
  opacity: 0.85;
}

/* Shiny Sweep Effect */
.premium-icon-wrap::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    transparent 35%,
    rgba(255,255,255,0.35),
    transparent 65%
  );
  transform: translateX(-120%);
  animation: shineMove 3.8s ease-in-out infinite;
}

/* Hover Boost */
.premium-icon-wrap:hover {
  transform: scale(1.08);
  transition: transform 0.3s ease;
}

/* ===============================
   📱 MOBILE OPTIMIZATION (FORCE)
================================ */
@media (max-width: 640px) {
  .premium-icon-wrap {
    width: 42px !important;
    height: 42px !important;
    background: radial-gradient(
      circle at top,
      rgba(0, 180, 255, 0.22),
      rgba(0, 50, 100, 0.28)
    );
  }

  .premium-icon-img {
    width: 20px !important;
    height: 20px !important;
  }

  .premium-glow {
    inset: -3px !important;
    filter: blur(6px) !important;
    opacity: 0.65 !important;
  }

  .premium-card {
    padding: 10px !important;
  }

  .premium-label {
    font-size: 11px !important;
  }
}
/* ===============================
   🔄 KEYFRAMES
================================ */
@keyframes premiumSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes shineMove {
  0% { transform: translateX(-120%); }
  50% { transform: translateX(120%); }
  100% { transform: translateX(120%); }
}
/* ===============================
   🧊 PREMIUM SERVICE CARD STYLE
================================ */
.premium-service-card {
  background: rgba(17, 24, 39, 0.55); /* same as stats cards */
}

/* Mobile polish */
@media (max-width: 640px) {
  .premium-service-card {
    padding: 14px;
  }

  .premium-service-card span {
    font-size: 12px;
  }
}
/* ===============================
   📢 ANNOUNCEMENT MOBILE FIX
================================ */

@media (max-width: 640px) {
  .announcement-bar {
    width: calc(100vw - 16px); /* full width minus padding */
    margin-left: auto;
    margin-right: auto;
  }
}
`}</style>
</div>
</div>
  );
}